// frontend/src/components/ProfileCardPreview.js

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  CaretCircleLeft,
  CaretCircleRight,
  Headphones,
  MegaphoneSimple,
  X,
} from "phosphor-react";

import ShoutoutWallModal from "./ShoutoutWallModal";
import { DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";
import { getShoutoutFont } from "../constants/shoutoutFonts";

const SHOUTOUTS_PER_PAGE = 12;

/*
 * Same small mobile variations used by ProfileFlowersPage.
 */
const MOBILE_MESSAGE_STYLES = [
  { rotate: "-2deg", y: "0px" },
  { rotate: "2deg", y: "8px" },

  { rotate: "1deg", y: "-4px" },
  { rotate: "-2deg", y: "6px" },

  { rotate: "-1deg", y: "3px" },
  { rotate: "3deg", y: "-5px" },

  { rotate: "2deg", y: "7px" },
  { rotate: "-1deg", y: "-3px" },

  { rotate: "-2deg", y: "4px" },
  { rotate: "2deg", y: "-5px" },

  { rotate: "1deg", y: "5px" },
  { rotate: "-2deg", y: "0px" },
];

function MobilePreviewShoutout({
  shoutout,
  index,
}) {
  const senderName =
    shoutout?.sender?.username ||
    shoutout?.sender?.name ||
    "someone";

  const shoutoutFont = getShoutoutFont(
    shoutout?.font_id
  );

  const position =
    MOBILE_MESSAGE_STYLES[
      index % MOBILE_MESSAGE_STYLES.length
    ];

  return (
    <div
      className="
        flex min-w-0
        flex-col
        items-center
        justify-center
        px-1
        text-center
      "
      style={{
        transform: `
          translateY(${position.y})
          rotate(${position.rotate})
        `,
      }}
    >
      {shoutout?.prefix && (
        <p
          className="
            whitespace-nowrap
            text-[9px]
            font-bold
            uppercase
            leading-tight
            tracking-[0.08em]
            text-blue-500
          "
        >
          {shoutout.prefix}
        </p>
      )}

      <p
        className="
          mt-0.5
          max-w-full
          whitespace-pre-wrap
          break-words
          text-[17px]
          leading-[1.1]
          text-[#25366f]
        "
        style={{
          fontFamily: shoutoutFont.css,
          fontWeight: shoutoutFont.weight,
        }}
      >
        {shoutout?.body}
      </p>

      <p
        className="
          mt-1
          max-w-full
          truncate
          text-[10px]
          font-medium
          leading-none
          text-gray-400
        "
      >
        @{senderName}
      </p>
    </div>
  );
}

function MobilePreviewShoutoutModal({
  open,
  onClose,
  shoutouts = [],
}) {
  const [page, setPage] =
    useState(0);

  const visibleShoutouts = useMemo(
    () =>
      shoutouts.filter(
        (shoutout) =>
          !shoutout.hidden_by_recipient
      ),
    [shoutouts]
  );

  /*
   * Keep the same chronological ordering as
   * ProfileFlowersPage.
   */
  const chronologicalShoutouts =
    useMemo(
      () =>
        [...visibleShoutouts].sort(
          (a, b) =>
            Number(a.id) - Number(b.id)
        ),
      [visibleShoutouts]
    );

  const totalPages = Math.max(
    1,
    Math.ceil(
      chronologicalShoutouts.length /
        SHOUTOUTS_PER_PAGE
    )
  );

  const pageShoutouts =
    useMemo(() => {
      const start =
        page * SHOUTOUTS_PER_PAGE;

      return chronologicalShoutouts.slice(
        start,
        start + SHOUTOUTS_PER_PAGE
      );
    }, [
      chronologicalShoutouts,
      page,
    ]);

  useEffect(() => {
    if (open) {
      setPage(0);
    }
  }, [open]);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(
        Math.max(
          0,
          totalPages - 1
        )
      );
    }
  }, [
    page,
    totalPages,
  ]);

  const goPrevious = () => {
    setPage((current) =>
      Math.max(
        0,
        current - 1
      )
    );
  };

  const goNext = () => {
    setPage((current) =>
      Math.min(
        totalPages - 1,
        current + 1
      )
    );
  };

  if (
    !open ||
    typeof document === "undefined"
  ) {
    return null;
  }

  return createPortal(
    <div
      className="
        fixed inset-0 z-[9999]
        flex items-center justify-center
        p-3
        md:hidden
      "
    >
      {/* BACKDROP */}
      <button
        type="button"
        className="
          absolute inset-0
          bg-black/40
        "
        onClick={onClose}
        aria-label="Close shoutouts"
      />

      {/* MODAL */}
      <div
        className="
          relative z-10
          flex
          h-[82svh]
          w-[full] mx-2
          max-w-md
          flex-col
          overflow-hidden
          rounded-[28px]
          bg-blue-50
          ring-4
          ring-blue-800
        "
      >
        {/* HEADER */}
        <div
          className="
            relative z-20
            shrink-0
            px-6
            pb-2
            pt-5
          "
        >
          <div className="pr-9">
            <div className="inline-flex flex-col items-stretch">
              <h2
                className="
                  whitespace-nowrap
                  text-[20px]
                  text-blue-600
                  dm-font
                  font-black
                  tracking-[-0.06em]
                  leading-[0.9]
                  select-none
                "
              >
                Shoutouts
              </h2>

              <svg
                viewBox="0 0 300 16"
                className="
                  -mt-0.5
                  block
                  h-3
                  w-full
                  text-blue-500
                "
                aria-hidden="true"
                preserveAspectRatio="none"
              >
                <path
                  d="
                    M 2 8
                    Q 17 0, 32 8
                    T 62 8
                    T 92 8
                    T 122 8
                    T 152 8
                    T 182 8
                    T 212 8
                    T 242 8
                    T 272 8
                    T 298 8
                  "
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>

          {/* CLOSE */}
          <button
            type="button"
            onClick={onClose}
            className="
              absolute
              right-5
              top-5
              inline-flex
              items-center
              justify-center
              !min-h-0
              !min-w-0
            "
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* CONTENT */}
        <div
          className="
            min-h-0
            flex-1
            overflow-y-auto
            pl-5 pr-4
            pb-5
            pt-4
          "
        >
          {visibleShoutouts.length === 0 ? (
            <div
              className="
                flex
                h-full
                items-center
                justify-center
                text-center
              "
            >
              <p
                className="
                  text-sm
                  font-semibold
                  text-gray-700
                "
              >
                No shoutouts yet
              </p>
            </div>
          ) : (
            <div
              className="
                mx-auto
                grid
                w-full
                max-w-md
                grid-cols-2
                content-start
                gap-x-3
                gap-y-6
                pb-3
                pt-0.5
              "
            >
              {pageShoutouts.map(
                (
                  shoutout,
                  index
                ) => (
                  <MobilePreviewShoutout
                    key={
                      shoutout.id
                    }
                    shoutout={
                      shoutout
                    }
                    index={index}
                  />
                )
              )}
            </div>
          )}
        </div>

        {/* PAGE CONTROLS */}
        {visibleShoutouts.length > 0 &&
          totalPages > 1 && (
            <div
              className="
                flex
                shrink-0
                items-center
                justify-center
                gap-3
                px-4
                pb-5
                pt-2
              "
            >
              <button
                type="button"
                onClick={goPrevious}
                disabled={page === 0}
                className="
                  inline-flex
                  items-center
                  justify-center
                  text-blue-600
                  transition
                  !min-h-0
                  !min-w-0
                  hover:text-blue-800
                  disabled:cursor-default
                  disabled:opacity-25
                "
                aria-label="Previous shoutouts page"
              >
                <CaretCircleLeft
                  size={26}
                  weight="duotone"
                />
              </button>

              <div
                className="
                  flex
                  items-center
                  gap-1.5
                "
              >
                {Array.from(
                  {
                    length:
                      totalPages,
                  },
                  (
                    _,
                    index
                  ) => (
                    <button
                      key={
                        index
                      }
                      type="button"
                      onClick={() =>
                        setPage(
                          index
                        )
                      }
                      aria-label={`Go to shoutouts page ${
                        index + 1
                      }`}
                      className={`
                        h-1.5
                        w-1.5
                        rounded-full
                        transition
                        !min-h-0
                        !min-w-0
                        ${
                          page ===
                          index
                            ? "bg-blue-600"
                            : "bg-blue-200 hover:bg-blue-300"
                        }
                      `}
                    />
                  )
                )}
              </div>

              <button
                type="button"
                onClick={goNext}
                disabled={
                  page ===
                  totalPages - 1
                }
                className="
                  inline-flex
                  items-center
                  justify-center
                  text-blue-600
                  transition
                  !min-h-0
                  !min-w-0
                  hover:text-blue-800
                  disabled:cursor-default
                  disabled:opacity-25
                "
                aria-label="Next shoutouts page"
              >
                <CaretCircleRight
                  size={26}
                  weight="duotone"
                />
              </button>
            </div>
          )}
      </div>
    </div>,
    document.body
  );
}

export default function ProfileCardPreview({
  user,
  shoutouts = [],
  onPreviewAction,
}) {
  const [shoutoutsOpen, setShoutoutsOpen] =
    useState(false);

  const [isMobile, setIsMobile] =
    useState(() =>
      typeof window !== "undefined"
        ? window.matchMedia(
            "(max-width: 767px)"
          ).matches
        : false
    );

  useEffect(() => {
    if (
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const mediaQuery =
      window.matchMedia(
        "(max-width: 767px)"
      );

    const updateIsMobile = (
      event
    ) => {
      setIsMobile(
        event.matches
      );
    };

    setIsMobile(
      mediaQuery.matches
    );

    mediaQuery.addEventListener(
      "change",
      updateIsMobile
    );

    return () => {
      mediaQuery.removeEventListener(
        "change",
        updateIsMobile
      );
    };
  }, []);

  if (!user) return null;

  const displayName =
    user.display_name ||
    user.name ||
    user.username ||
    "User";

  const displayUsername =
    user.username || "user";

  const displayBio =
    user.bio?.trim() || "";

  const hasLately =
    Array.isArray(
      user.currently_exploring
    ) &&
    user.currently_exploring.length >
      0;

  const hasHome =
    Array.isArray(
      user.home_places
    ) &&
    user.home_places.length > 0;

  const hasOnRepeat =
    Boolean(user.on_repeat_url);

  return (
    <>
      <section className="rounded-2xl bg-white px-5 pt-5 pb-4">
        <div className="flex flex-col items-center text-center">
          {/* PROFILE PHOTO */}
          <img
            src={
              user.profile_pic_url ||
              DEFAULT_PROFILE_IMAGE
            }
            alt={displayName}
            className="h-20 w-20 rounded-full object-cover"
          />

          {/* NAME */}
          <h2 className="mt-3 text-lg font-black tracking-[-0.03em] text-gray-900">
            {displayName}
          </h2>

          {/* USERNAME */}
          <p className="-mt-0.5 text-sm text-gray-500">
            @{displayUsername}
          </p>

          {/* BIO */}
          {displayBio && (
            <p className="mt-2 max-w-sm whitespace-pre-line text-sm leading-tight text-gray-600">
              {displayBio}
            </p>
          )}

          {/* PROFILE EXTRAS */}
          {(hasLately ||
            hasHome ||
            hasOnRepeat) && (
            <div className="mt-3.5 space-y-2.5 text-center text-xs">

              {/* LATELY */}
              {hasLately && (
                <div className="w-full">
                  <div className="mb-1 text-center">
                    <span className="font-semibold text-blue-600">
                      lately
                    </span>
                  </div>

                  {user
                    .currently_exploring
                    .length === 5 ? (
                    <div className="mx-auto flex max-w-[280px] flex-col items-center gap-1.5">
                      <div className="flex justify-center gap-1.5">
                        {user.currently_exploring
                          .slice(
                            0,
                            3
                          )
                          .map(
                            (
                              item
                            ) => (
                              <button
                                key={
                                  item
                                }
                                type="button"
                                className="
                                  rounded-full bg-blue-600
                                  !min-h-0 !min-w-0
                                  px-2 py-0.5
                                  text-xs font-medium
                                  text-white
                                  hover:bg-blue-200
                                "
                              >
                                {
                                  item
                                }
                              </button>
                            )
                          )}
                      </div>

                      <div className="flex justify-center gap-1.5">
                        {user.currently_exploring
                          .slice(
                            3
                          )
                          .map(
                            (
                              item
                            ) => (
                              <button
                                key={
                                  item
                                }
                                type="button"
                                className="
                                  rounded-full bg-blue-600
                                  !min-h-0 !min-w-0
                                  px-2 py-0.5
                                  text-xs font-medium
                                  text-white
                                  hover:bg-blue-200
                                "
                              >
                                {
                                  item
                                }
                              </button>
                            )
                          )}
                      </div>
                    </div>
                  ) : (
                    <div className="mx-auto flex max-w-[280px] flex-wrap justify-center gap-1.5">
                      {user.currently_exploring.map(
                        (
                          item
                        ) => (
                          <button
                            key={
                              item
                            }
                            type="button"
                            className="
                              rounded-full bg-blue-600
                              !min-h-0 !min-w-0
                              px-2 py-0.5
                              text-xs font-medium
                              text-white
                              hover:bg-blue-200
                            "
                          >
                            {
                              item
                            }
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* HOME */}
              {hasHome && (
                <div>
                  <span className="font-semibold text-blue-600">
                    home
                  </span>

                  <div className="flex flex-wrap items-center justify-center gap-x-1 font-medium text-gray-800">
                    {user.home_places.map(
                      (
                        place,
                        index
                      ) => (
                        <React.Fragment
                          key={
                            place
                          }
                        >
                          {index >
                            0 && (
                            <span className="text-blue-500">
                              ·
                            </span>
                          )}

                          <span>
                            {
                              place
                            }
                          </span>
                        </React.Fragment>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* ON REPEAT */}
              {hasOnRepeat && (
                <div
                  className={`w-full min-w-0 ${
                    hasLately &&
                    !hasHome
                      ? "!mt-3.5 !mb-1"
                      : "!mt-2.5 !mb-1"
                  }`}
                >
                  <a
                    href={
                      user.on_repeat_url
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`
                      mx-auto flex w-fit
                      !min-h-0 !min-w-0
                      flex-col
                      transition-colors

                      ${
                        user.on_repeat_type ===
                        "track"
                          ? `
                            max-w-[280px]
                            items-center
                            rounded-3xl
                            bg-blue-100
                            px-4 py-1.5
                            text-center
                            hover:bg-blue-200
                          `
                          : `
                            max-w-[240px]
                            items-center
                            rounded-3xl
                            bg-blue-100
                            px-4 py-1.5
                            text-center
                            hover:bg-blue-200
                          `
                      }
                    `}
                  >
                    <div className="mb-0.5 flex items-center justify-center text-blue-700">
                      <Headphones
                        size={
                          12
                        }
                        weight="bold"
                      />
                    </div>

                    <div className="min-w-0">
                      <p className="whitespace-normal break-words text-xs font-semibold leading-tight text-gray-900">
                        {user.on_repeat_title ||
                          "Spotify playlist"}
                      </p>

                      {user.on_repeat_type ===
                        "track" &&
                        user.on_repeat_artist && (
                          <p className="whitespace-normal break-words text-[10px] leading-tight text-gray-500">
                            {
                              user.on_repeat_artist
                            }
                          </p>
                        )}
                    </div>
                  </a>
                </div>
              )}
            </div>
          )}

          {/* SHOUTOUTS */}
          {user.flowers_enabled !==
            false && (
            <div className="mt-4 flex w-full flex-col items-center justify-center">
              <button
                type="button"
                onClick={() =>
                  setShoutoutsOpen(
                    true
                  )
                }
                className="
                  inline-flex items-center gap-1
                  !min-h-0 !min-w-0
                  rounded-full
                  bg-blue-800
                  px-3 py-1
                  text-xs font-semibold
                  text-white
                  ring-2 ring-blue-300
                  animate-[wiggle_1s_ease-in-out_infinite]
                "
              >
                <MegaphoneSimple
                  size={13}
                  weight="duotone"
                />
                Shoutouts
              </button>
            </div>
          )}

          {/* STATS */}
          <div className="mx-auto grid w-full grid-cols-3 gap-2 pt-3">
            <div className="flex flex-col items-center px-1 pt-2 leading-tight">
              <span className="font-black text-blue-600">
                {user.activitiesCount ??
                  0}
              </span>

              <span className="text-[12px] text-gray-500">
                activities
              </span>
            </div>

            <div className="flex flex-col items-center px-1 pt-2 leading-tight">
              <span className="font-black text-blue-600">
                {user.followers ??
                  0}
              </span>

              <span className="text-[12px] text-gray-500">
                followers
              </span>
            </div>

            <div className="flex flex-col items-center px-1 pt-2 leading-tight">
              <span className="font-black text-blue-600">
                {user.following ??
                  0}
              </span>

              <span className="text-[12px] text-gray-500">
                following
              </span>
            </div>
          </div>

          {/* OPTIONAL LATEST */}
          {user.latest_activity_title && (
            <div
              className="
                mt-3 max-w-full
                text-left text-[13px]
                text-gray-600
              "
            >
              <span className="font-semibold text-gray-800">
                Latest:
              </span>{" "}
              <span className="text-blue-600">
                {
                  user.latest_activity_title
                }
              </span>
            </div>
          )}
        </div>
      </section>

      {/* MOBILE PREVIEW ONLY */}
      {isMobile && (
        <MobilePreviewShoutoutModal
          open={shoutoutsOpen}
          onClose={() =>
            setShoutoutsOpen(
              false
            )
          }
          shoutouts={shoutouts}
        />
      )}

      {/* DESKTOP — KEEP EXISTING WALL EXACTLY */}
      {!isMobile && (
        <ShoutoutWallModal
          open={shoutoutsOpen}
          onClose={() =>
            setShoutoutsOpen(
              false
            )
          }
          profileUser={user}
          canGiveShoutout={false}
          shoutoutsOverride={
            shoutouts
          }
        />
      )}
    </>
  );
}